import { useState, useCallback, useEffect } from 'react';
import { ScryfallCard, ScryfallCardWithCount } from '@/types/scryfall';
import { useLocalStorage } from './useLocalStorage';

export const useCardList = (storageKey: string, initialCards: ScryfallCardWithCount[] = []) => {
  const [storedCards, setStoredCards] = useLocalStorage<ScryfallCardWithCount[]>(storageKey, initialCards);
  const [cards, setCards] = useState<ScryfallCardWithCount[]>(storedCards);

  useEffect(() => {
    setStoredCards(cards);
  }, [cards, setStoredCards]);


  const addCard = useCallback((card: ScryfallCard) => {
    setCards(prev => {
      const existingCard = prev.find(c => c.id === card.id && c.is_foil === card.is_foil);
      if (existingCard) {
        return prev.map(c => {
          if (c.id === card.id && c.is_foil === card.is_foil) {
            return { ...c, count: c.count + 1 };
          }
          return c;
        });
      }
      return [...prev, { ...card, count: 1 }];
    });
  }, []);

  const removeCard = useCallback((cardToRemove: ScryfallCardWithCount) => {
    setCards(prev => {
      const existingCard = prev.find(c => c.id === cardToRemove.id && c.is_foil === cardToRemove.is_foil);
      if (existingCard && existingCard.count > 1) {
        return prev.map(c => {
          if (c.id === cardToRemove.id && c.is_foil === cardToRemove.is_foil) {
            return { ...c, count: c.count - 1 };
          }
          return c;
        });
      }
      return prev.filter(c => c.id !== cardToRemove.id || c.is_foil !== cardToRemove.is_foil);
    });
  }, []);

  const setCardCount = useCallback((cardToUpdate: ScryfallCardWithCount, count: number) => {
    setCards(prev => {
      return prev.map(c => {
        if (c.id === cardToUpdate.id && c.is_foil === cardToUpdate.is_foil) {
          return { ...c, count };
        }
        return c;
      });
    });
  }, []);

  const updateCard = useCallback((oldCardId: string, newCard: ScryfallCardWithCount) => {
    setCards(prev => prev.map(c => (c.id === oldCardId ? newCard : c)));
  }, []);

  const clearList = useCallback(() => {
    setCards([]);
  }, []);

  const setList = useCallback((newCards: ScryfallCardWithCount[]) => {
    setCards(newCards);
  }, []);

  return { cards, addCard, removeCard, setCardCount, updateCard, clearList, setList };
};
